import { useState, useEffect, useCallback } from "react";
import { C } from "./ui/theme";

// Phase 3 (standalone page-file token migration, see linear-brewing-toast.md)
// — this file used to define its own local Fluent/Azure-blue palette here
// (`const T = {...}`, accent:"#0078D4"), completely separate from
// ui/theme.js's Apple palette `C`. Every `C.xxx` reference below has been
// renamed to `C.xxx` (Phase 1 gave `C` a same-named counterpart for every
// key `T` had, specifically so this could be a pure token-name swap with
// zero value remapping) and the local palette object removed.

function Badge({children,bg,tc,sm}){
  return <span style={{background:bg,color:tc,fontSize:sm?10:11,padding:sm?"2px 7px":"3px 9px",borderRadius:99,fontWeight:500,whiteSpace:"nowrap"}}>{children}</span>;
}

// Spinner previously had a hardcoded dark-navy border ("#1A1E2E") and a
// blue-purple default color ("#4F6EF7") — neither value existed anywhere
// else in this file or the app's light Apple palette, so this looks like a
// leftover from an earlier dark-theme pass (the same category of stray bug
// as Toast's empty icon spans found in Phase 1). Restyled onto C tokens to
// match every other Spinner in the app (App.js's shared Spinner, ContextMenu
// etc.) — same size/animation/API, just the resting colors fixed.
function Spinner({size=13,color=C.accent}){
  return <div style={{width:size,height:size,border:`2px solid ${C.border}`,borderTopColor:color,borderRadius:"50%",display:"inline-block",flexShrink:0,animation:"spin .7s linear infinite"}}/>;
}

function Progress({pct,color=C.accent,h=5}){
  return(
    <div style={{height:h,background:C.bgHover,borderRadius:h,overflow:"hidden"}}>
      <div style={{width:Math.min(pct,100)+"%",height:"100%",background:color,borderRadius:h,transition:"width .5s ease"}}/>
    </div>
  );
}

const STATUS_CFG = {
  Draft:     { bg:C.bgHover,     tc:C.textSub, dot:C.textSub },
  Active:    { bg:C.greenMuted,  tc:C.green,   dot:C.green },
  Paused:    { bg:C.amberMuted,  tc:C.amber,   dot:C.amber },
  Completed: { bg:C.accentMuted, tc:C.accent,  dot:C.accent },
};

export default function CampaignsPage({addToast}){
  const toast = addToast || function(){};
  const [campaigns,setCampaigns]=useState([]);
  const [loading,setLoading]=useState(false);
  const [search,setSearch]=useState("");
  const [statusFilter,setStatusFilter]=useState("all");
  const [actionLoading,setActionLoading]=useState({});
  const [expandedId,setExpandedId]=useState(null);
  const [editingId,setEditingId]=useState(null);
  const [editName,setEditName]=useState("");

  const fetchCampaigns=useCallback(async()=>{
    setLoading(true);
    try{
      const r=await fetch("/api/campaigns");
      const d=await r.json();
      setCampaigns(Array.isArray(d)?d:[]);
    }catch(e){}
    setLoading(false);
  },[]);

  useEffect(()=>{fetchCampaigns();},[fetchCampaigns]);

  const doAction=async(id,action)=>{
    setActionLoading(p=>({...p,[id+action]:true}));
    try{
      const r=await fetch("/api/campaigns/"+id+"/"+action,{method:"POST"});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      setCampaigns(p=>p.map(c=>c.id===id?d:c));
      toast("Campaign "+action+"ed","success");
    }catch(e){toast(e.message,"error");}
    setActionLoading(p=>({...p,[id+action]:false}));
  };

  const deleteCampaign=async(id)=>{
    try{
      const r=await fetch("/api/campaigns/"+id,{method:"DELETE"});
      if(!r.ok)throw new Error("Delete failed");
      setCampaigns(p=>p.filter(c=>c.id!==id));
      toast("Campaign deleted","info");
    }catch(e){toast(e.message,"error");}
  };

  const saveEdit=async(id)=>{
    if(!editName.trim()){toast("Name cannot be empty","error");return;}
    try{
      const r=await fetch("/api/campaigns/"+id,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({name:editName})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      setCampaigns(p=>p.map(c=>c.id===id?d:c));
      setEditingId(null);
      toast("Campaign renamed","success");
    }catch(e){toast(e.message,"error");}
  };

  const filtered=campaigns.filter(c=>{
    const matchSearch=(c.name||"").toLowerCase().includes(search.toLowerCase())||
      (c.sequenceName||"").toLowerCase().includes(search.toLowerCase())||
      (c.fromEmail||"").toLowerCase().includes(search.toLowerCase());
    const matchStatus=statusFilter==="all"||c.status===statusFilter;
    return matchSearch&&matchStatus;
  });

  const stats={
    total:campaigns.length,
    active:campaigns.filter(c=>c.status==="Active").length,
    paused:campaigns.filter(c=>c.status==="Paused").length,
    draft:campaigns.filter(c=>c.status==="Draft").length,
    completed:campaigns.filter(c=>c.status==="Completed").length,
  };

  return(
    <div style={{fontFamily:"'Geist',sans-serif",animation:"fadeUp .25s ease both"}}>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}"}</style>

      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:22,flexWrap:"wrap",gap:10}}>
        <div>
          {/* Plain professional title — inherits the page's own 'Geist'
              sans-serif; no italic script face, no rainbow gradient fill. */}
          <p style={{fontSize:21,fontWeight:650,letterSpacing:"-.3px",lineHeight:1.25,color:C.text}}>Campaigns</p>
          <p style={{fontSize:13,color:C.textSub,marginTop:3}}>
            {stats.total} campaigns  {stats.active} active  {stats.paused} paused
          </p>
        </div>
        <button onClick={fetchCampaigns} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer"}}>
           Refresh
        </button>
      </div>

      {/* Stats grid */}
      <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginBottom:20}}>
        {[
          {label:"Total",    value:stats.total,     color:C.accent},
          {label:"Active",   value:stats.active,    color:C.green},
          {label:"Paused",   value:stats.paused,    color:C.amber},
          {label:"Draft",    value:stats.draft,     color:C.textSub},
          {label:"Completed",value:stats.completed, color:C.purple},
        ].map(s=>(
          <div key={s.label} onClick={()=>setStatusFilter(s.label==="Total"?"all":s.label)}
            style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:10,padding:"12px 16px",cursor:"pointer",transition:"all .15s"}}>
            <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:5}}>{s.label}</p>
            <p style={{fontSize:22,fontWeight:700,color:s.color,fontFamily:"monospace",lineHeight:1}}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search campaigns..."
          style={{flex:1,minWidth:200,background:C.bgCard,border:"1px solid "+C.border,borderRadius:8,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}/>
        {["all","Draft","Active","Paused","Completed"].map(s=>(
          <button key={s} onClick={()=>setStatusFilter(s)}
            style={{padding:"7px 14px",borderRadius:8,border:"1px solid "+(statusFilter===s?C.accent:C.border),background:statusFilter===s?C.accentMuted:"transparent",color:statusFilter===s?C.accent:C.textSub,fontSize:12,cursor:"pointer",textTransform:"capitalize"}}>
            {s}
          </button>
        ))}
      </div>

      {/* Campaign list */}
      {loading&&(
        <div style={{padding:40,textAlign:"center",color:C.textSub,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          <Spinner/> Loading campaigns...
        </div>
      )}

      {!loading&&filtered.length===0&&(
        <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:"48px 20px",textAlign:"center"}}>
          <div style={{fontSize:40,marginBottom:10}}></div>
          <p style={{fontSize:14,color:C.textSub,marginBottom:4}}>No campaigns yet</p>
          <p style={{fontSize:12,color:C.textMuted,marginBottom:16}}>Create a sequence in the Sequences page to automatically generate a campaign</p>
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {filtered.map(c=>{
          const sc=STATUS_CFG[c.status]||STATUS_CFG.Draft;
          const pct=c.totalContacts>0?Math.round((c.sent/c.totalContacts)*100):0;
          const isExpanded=expandedId===c.id;
          const isEditing=editingId===c.id;
          return(
            <div key={c.id} style={{background:C.bgCard,border:"1px solid "+(c.status==="Active"?C.green+"44":C.border),borderRadius:12,overflow:"hidden"}}>

              {/* Main row */}
              <div style={{padding:"16px 20px"}}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12,marginBottom:14,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:200}}>
                    {isEditing?(
                      <div style={{display:"flex",gap:8,marginBottom:6}}>
                        <input value={editName} onChange={e=>setEditName(e.target.value)}
                          style={{background:C.bgHover,border:"1px solid "+C.accent,borderRadius:6,padding:"5px 10px",color:C.text,fontSize:14,fontWeight:500,outline:"none",flex:1}}/>
                        <button onClick={()=>saveEdit(c.id)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:6,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>Save</button>
                        <button onClick={()=>setEditingId(null)} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:6,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>Cancel</button>
                      </div>
                    ):(
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                        <p style={{fontSize:15,fontWeight:600,color:C.text}}>{c.name}</p>
                        <Badge bg={sc.bg} tc={sc.tc} sm>
                          <span style={{width:5,height:5,borderRadius:"50%",background:sc.dot,display:"inline-block",marginRight:4}}/>
                          {c.status}
                        </Badge>
                      </div>
                    )}
                    <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                      {c.sequenceName&&<span style={{fontSize:12,color:C.textSub}}> {c.sequenceName}</span>}
                      {c.contactFileName&&<span style={{fontSize:12,color:C.textSub}}> {c.contactFileName}</span>}
                      {c.fromEmail&&<span style={{fontSize:12,color:C.textSub}}> {c.fromEmail}</span>}
                      <span style={{fontSize:12,color:C.textSub}}> {c.totalContacts||0} contacts</span>
                      <span style={{fontSize:12,color:C.textSub}}> {c.startedAt?new Date(c.startedAt).toLocaleDateString():new Date(c.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",flexShrink:0}}>
                    {c.status==="Draft"&&(
                      <button onClick={()=>doAction(c.id,"start")} disabled={!!actionLoading[c.id+"start"]}
                        style={{background:C.green,color:"#fff",border:"none",borderRadius:7,padding:"6px 14px",fontSize:12,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                        {actionLoading[c.id+"start"]?<><Spinner size={11} color="#fff"/> Starting...</>:" Start Campaign"}
                      </button>
                    )}
                    {c.status==="Active"&&(
                      <button onClick={()=>doAction(c.id,"pause")} disabled={!!actionLoading[c.id+"pause"]}
                        style={{background:C.amberMuted,color:C.amber,border:"1px solid "+C.amber+"44",borderRadius:7,padding:"6px 14px",fontSize:12,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                        {actionLoading[c.id+"pause"]?<><Spinner size={11} color={C.amber}/> Pausing...</>:" Pause"}
                      </button>
                    )}
                    {c.status==="Paused"&&(
                      <button onClick={()=>doAction(c.id,"resume")} disabled={!!actionLoading[c.id+"resume"]}
                        style={{background:C.greenMuted,color:C.green,border:"1px solid "+C.green+"44",borderRadius:7,padding:"6px 14px",fontSize:12,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                        {actionLoading[c.id+"resume"]?<><Spinner size={11} color={C.green}/> Resuming...</>:" Resume"}
                      </button>
                    )}
                    {c.status==="Active"&&(
                      <button onClick={()=>doAction(c.id,"complete")}
                        style={{background:C.accentMuted,color:C.accent,border:"none",borderRadius:7,padding:"6px 14px",fontSize:12,cursor:"pointer"}}>
                        Complete
                      </button>
                    )}
                    <button onClick={()=>{setEditingId(c.id);setEditName(c.name);}}
                      style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"6px 12px",fontSize:12,cursor:"pointer"}}>
                      Edit
                    </button>
                    <button onClick={()=>setExpandedId(isExpanded?null:c.id)}
                      style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"6px 12px",fontSize:12,cursor:"pointer"}}>
                      {isExpanded?"Hide":"Analytics"}
                    </button>
                    <button onClick={()=>deleteCampaign(c.id)}
                      style={{background:C.redMuted,color:C.red,border:"none",borderRadius:7,padding:"6px 12px",fontSize:12,cursor:"pointer"}}>
                      Delete
                    </button>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:11,color:C.textSub}}>Progress</span>
                    <span style={{fontSize:11,color:C.textSub,fontFamily:"monospace"}}>{c.sent||0} / {c.totalContacts||0} sent ({pct}%)</span>
                  </div>
                  <Progress pct={pct} color={c.status==="Active"?C.green:C.accent}/>
                </div>

                {/* Quick stats */}
                <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"repeat(8,1fr)",gap:6}}>
                  {[
                    {label:"Sent",      value:c.sent||0,          color:C.accent},
                    {label:"Delivered", value:c.delivered||0,     color:C.cyan},
                    {label:"Opens",     value:c.opened||0,        color:C.amber},
                    {label:"Clicks",    value:c.clicked||0,       color:C.purple},
                    {label:"Replies",   value:c.replied||0,       color:C.green},
                    {label:"Bounced",   value:c.bounced||0,       color:C.red},
                    {label:"Unsub",     value:c.unsubscribed||0,  color:C.textSub},
                    {label:"Opps",      value:c.opportunities||0, color:C.green},
                  ].map(s=>(
                    <div key={s.label} style={{background:C.bgHover,borderRadius:7,padding:"7px 10px",textAlign:"center"}}>
                      <p style={{fontSize:15,fontWeight:700,color:s.color,fontFamily:"monospace",lineHeight:1}}>{s.value}</p>
                      <p style={{fontSize:10,color:C.textSub,marginTop:3}}>{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Expanded analytics */}
              {isExpanded&&(
                <div style={{padding:"14px 20px",borderTop:"1px solid "+C.border,background:C.bgHover}}>
                  <p style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:12}}>Campaign Analytics</p>
                  <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:14}}>
                    {[
                      {label:"Open Rate",    value:c.sent?((c.opened||0)/c.sent*100).toFixed(1)+"%":"0%",    color:C.amber},
                      {label:"Click Rate",   value:c.sent?((c.clicked||0)/c.sent*100).toFixed(1)+"%":"0%",   color:C.purple},
                      {label:"Reply Rate",   value:c.opened?((c.replied||0)/c.opened*100).toFixed(1)+"%":"0%", color:C.green},
                      {label:"Bounce Rate",  value:c.sent?((c.bounced||0)/c.sent*100).toFixed(1)+"%":"0%",   color:C.red},
                    ].map(s=>(
                      <div key={s.label} style={{background:C.bgCard,borderRadius:8,padding:"12px 14px",textAlign:"center",border:"1px solid "+C.border}}>
                        <p style={{fontSize:22,fontWeight:700,color:s.color,fontFamily:"monospace",lineHeight:1}}>{s.value}</p>
                        <p style={{fontSize:11,color:C.textSub,marginTop:4}}>{s.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    <div style={{background:C.bgCard,borderRadius:8,padding:"10px 14px",border:"1px solid "+C.border}}>
                      <p style={{fontSize:11,color:C.textSub,marginBottom:6}}>Campaign info</p>
                      <p style={{fontSize:12,color:C.text}}>Sequence: <span style={{color:C.accent}}>{c.sequenceName||""}</span></p>
                      <p style={{fontSize:12,color:C.text,marginTop:4}}>Contact file: <span style={{color:C.accent}}>{c.contactFileName||""}</span></p>
                      <p style={{fontSize:12,color:C.text,marginTop:4}}>Sender: <span style={{color:C.accent}}>{c.fromEmail||""}</span></p>
                    </div>
                    <div style={{background:C.bgCard,borderRadius:8,padding:"10px 14px",border:"1px solid "+C.border}}>
                      <p style={{fontSize:11,color:C.textSub,marginBottom:6}}>Timeline</p>
                      <p style={{fontSize:12,color:C.text}}>Created: <span style={{color:C.textSub}}>{new Date(c.createdAt).toLocaleString()}</span></p>
                      {c.startedAt&&<p style={{fontSize:12,color:C.text,marginTop:4}}>Started: <span style={{color:C.green}}>{new Date(c.startedAt).toLocaleString()}</span></p>}
                      {c.pausedAt&&<p style={{fontSize:12,color:C.text,marginTop:4}}>Paused: <span style={{color:C.amber}}>{new Date(c.pausedAt).toLocaleString()}</span></p>}
                      {c.completedAt&&<p style={{fontSize:12,color:C.text,marginTop:4}}>Completed: <span style={{color:C.accent}}>{new Date(c.completedAt).toLocaleString()}</span></p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}